import { ContextMenuHandler, type IMenuItem } from "@asup/context-menu";
import { isEqual } from "lodash";
import {
  type CSSProperties,
  type ReactNode,
  useContext,
  useEffect,
  useMemo,
  useReducer,
} from "react";
import { EditorDefns, mapById } from "../../utility";
import {
  Analysis,
  AnalysisMethod,
  AnalysisSet,
  CaseWhenCondition,
  ConditionComparatorEnum,
  DataSubset,
  GroupingFactor,
} from "../ars_ldm";
import { ArsContext } from "./ArsContext";
import { arsContextReducer } from "./arsContextReducer";
import {
  AnnotationVariable,
  ARS_CHANGE_SENT,
  ArsContextState,
  ArsDataState,
  ArsStateUpdate,
  IArsContext,
  LOAD_ARS_PATCH,
  TOGGLE_SIDEBAR,
} from "./interface";

const contextMenuStyle: CSSProperties = {
  outline: "none",
  width: "100%",
  height: "100%",
};

/**
 * Props for the ArsContextProvider component.
 */
export interface ArsContextProviderProps<T = string> {
  /** Child components to render within the provider */
  children: ReactNode;
  analyses: Analysis[];
  analysisMethods: AnalysisMethod[];
  analysisSets: AnalysisSet[];
  caseWhenConditions: CaseWhenCondition<T>[];
  comparators: ConditionComparatorEnum[];
  dataSubsets: DataSubset[];
  disableAnalysisMethodEdit?: boolean;
  editorDefns: EditorDefns<T>;
  groupingFactors: GroupingFactor[];
  initialSidebarView?: "groupingFactor" | "operation" | "variable";
  updateARS: (state: ArsContextState<T>) => void;
  variables: AnnotationVariable[];
}

/**
 * Provides ARS (Analysis Results Standard) context to child components.
 * Manages state for analysis sets, data subsets, grouping factors,
 * and related reference data.
 *
 * @param props - The component props
 * @param props.children - The child components to render within the provider
 * @param props.analyses - Initial list of analyses
 * @param props.analysisMethods - Initial list of analysis methods
 * @param props.analysisSets - Initial list of analysis sets
 * @param props.caseWhenConditions - Initial list of case when conditions
 * @param props.comparators - Initial list of condition comparators
 * @param props.dataSubsets - Initial list of data subsets
 * @param props.disableAnalysisMethodEdit - Optional flag to disable Analysis Method editing
 * @param props.editorDefns - Editor definitions for the ARS context
 * @param props.groupingFactors - Initial list of grouping factors
 * @param props.initialSidebarView - Initial view for the sidebar ("groupingFactor", "operation", or "variable")
 * @param props.updateARS - Function to call when ARS state changes
 * @param props.variables - Initial list of annotation variables
 * @returns The ArsContextProvider component wrapping its children with context
 */
export const ArsContextProvider = <T = string,>({
  children,
  analyses,
  analysisMethods,
  analysisSets,
  caseWhenConditions,
  comparators,
  dataSubsets,
  disableAnalysisMethodEdit = false,
  editorDefns,
  groupingFactors,
  initialSidebarView = "groupingFactor",
  updateARS,
  variables,
}: ArsContextProviderProps<T>): ReactNode => {
  const initialDataState: ArsDataState<T> = useMemo(
    () => ({
      analyses: mapById(analyses),
      analysisMethods: mapById(analysisMethods),
      analysisSets: mapById(analysisSets),
      caseWhenConditions: mapById(caseWhenConditions),
      changed: false,
      comparators,
      dataSubsets: mapById(dataSubsets),
      datasetsInUse: new Set<string>(),
      disableAnalysisMethodEdit,
      editorDefns: editorDefns,
      groupingFactors: mapById(groupingFactors),
      uiRequestStatus: { requesting: false, error: false },
      variables,
      variablesInUse: new Set<string>(),
    }),
    [
      analyses,
      analysisMethods,
      analysisSets,
      caseWhenConditions,
      comparators,
      dataSubsets,
      disableAnalysisMethodEdit,
      editorDefns,
      groupingFactors,
      variables,
    ],
  );

  const [state, dispatch] = useReducer(arsContextReducer, {
    ...initialDataState,
    editorDefns,
    patches: [],
    ixPatch: -1,
    currentSidebar: {
      filter: "",
      isOpen: true,
      view: initialSidebarView,
    },
  });

  /**
   * Toggles the sidebar open state from the ARS context menu.
   */
  const handleToggleSidebar = (): void => {
    dispatch({ operation: TOGGLE_SIDEBAR });
  };

  const contextMenuItems: IMenuItem[] = [
    {
      label: state.currentSidebar.isOpen ? "Hide sidebar" : "Show sidebar",
      action: handleToggleSidebar,
    },
  ];

  useEffect(() => {
    const eq = isEqual(state, initialDataState);
    // Send changes up
    /* istanbul ignore else */
    if (state.changed && !eq) {
      updateARS(state);
      dispatch({ operation: ARS_CHANGE_SENT });
    } else if (!state.changed && !eq) {
      const updates: ArsStateUpdate[] = [];

      if (!isEqual(mapById(analyses), state.analyses)) {
        updates.push({ dataPath: "/analyses", value: mapById(analyses) });
      }
      if (!isEqual(mapById(analysisMethods), state.analysisMethods)) {
        updates.push({ dataPath: "/analysisMethods", value: mapById(analysisMethods) });
      }
      if (!isEqual(mapById(analysisSets), state.analysisSets)) {
        updates.push({ dataPath: "/analysisSets", value: mapById(analysisSets) });
      }
      if (!isEqual(mapById(caseWhenConditions), state.caseWhenConditions)) {
        updates.push({ dataPath: "/caseWhenConditions", value: mapById(caseWhenConditions) });
      }
      if (!isEqual(mapById(dataSubsets), state.dataSubsets)) {
        updates.push({ dataPath: "/dataSubsets", value: mapById(dataSubsets) });
      }
      if (!isEqual(mapById(groupingFactors), state.groupingFactors)) {
        updates.push({ dataPath: "/groupingFactors", value: mapById(groupingFactors) });
      }
      if (!isEqual(variables, state.variables)) {
        updates.push({ dataPath: "/variables", value: variables });
      }
      if (!isEqual(comparators, state.comparators)) {
        updates.push({ dataPath: "/comparators", value: comparators });
      }

      if (updates.length > 0) {
        dispatch({
          operation: LOAD_ARS_PATCH,
          updates,
        });
      }
    }
  }, [
    analyses,
    analysisMethods,
    analysisSets,
    caseWhenConditions,
    comparators,
    dataSubsets,
    groupingFactors,
    initialDataState,
    state,
    updateARS,
    variables,
  ]);

  return (
    <ArsContext.Provider value={{ state, dispatch }}>
      <ContextMenuHandler
        tabIndex={-1}
        style={contextMenuStyle}
        menuItems={contextMenuItems}
      >
        {children}
      </ContextMenuHandler>
    </ArsContext.Provider>
  );
};

ArsContextProvider.displayName = "ArsContextProvider";

/**
 * Hook to access the ARS context.
 *
 * @returns The ArsContext value containing state and dispatch
 */
export const useArsContext = <T,>(): IArsContext<T> => {
  const context = useContext(ArsContext);
  return context;
};
